import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useWorkplace } from "../context/WorkplaceContext";
import cctvSample from "../images/cctv_no.jpg";
import timeline from "../images/explorer_timeline.png";
import IonIcon from "../components/IonIcon";
import apiClient from "../apiClient";
import Calendar from "react-calendar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { captureFrame } from "../utils/captureFrame";
import { recordingVideo } from "../utils/recordingVideo";
import "../styles/flash.css";
dayjs.extend(utc);

/* 분할 버튼 옵션 */
const gridOptions = [1, 4, 9, 16, 25, 36, 49, 64];

/* 라이브 모드용 URL 생성기 */
const makeLiveWsUrl = (cam) => {
  const useWS = cam.serverIP.includes("192.168.0.2");
  const protocol = useWS ? "ws" : "wss";

  return (
    `${protocol}://${cam.serverIP}/stream/${cam.camUUID}/channel/0/mse` +
    `?uuid=${cam.camUUID}&channel=0`
  );
};

const RecordingVideo = () => {
  /* -------------------- 상태 -------------------- */
  const [gridCount, setGridCount] = useState(16);
  const { selectedWorkplaceId } = useWorkplace();
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [cameraList, setCameraLists] = useState([]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [currentMenu] = useState("recordVideo");
  const [isCheckboxUpdating, setIsCheckboxUpdating] = useState(false);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("00:00:00");
  const [timeInSeconds, setTimeInSeconds] = useState(0);
  const [isPlayback, setIsPlayback] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackSpeedRef = useRef(playbackSpeed);
  const [showNoCameraModal, setShowNoCameraModal] = useState(false);
  const [showNoCameraSaveModal, setShowNoCameraSaveModal] = useState(false);

  const [isPaused, setIsPaused] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [containVideo, setContainVideo] = useState(false);

  const isPlaybackRef = useRef(false);
  const timeInSecondsRef = useRef(0);
  const selectedDateRef = useRef(new Date());

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);
  useEffect(() => {
    isPlaybackRef.current = isPlayback;
  }, [isPlayback]);
  useEffect(() => {
    timeInSecondsRef.current = timeInSeconds;
  }, [timeInSeconds]);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const maxDuration = 24 * 60 * 60;

  const makePlaybackWsUrl = (cam, sec, rate) => {
    const target = dayjs(selectedDate)
      .startOf("day")
      .add(sec, "second")
      .utc()
      .format("YYYYMMDDTHHmmss[Z]");

    const rtsp = `rtsp://${cam.recordIP}:558/PlaybackChannel/${cam.camChannel}/media.smp/start=${target}`;

    //alert(cam.serverIP);
    //const wsUrl = `ws://${cam.serverIP}:8083/stream/playback/mse?rtsp=${encodeURIComponent(rtsp)}&session=${session}`;
    //const wsUrl = `ws://${cam.serverIP}:8083/stream/playback/mse?rtsp=${rtsp}&session=${session}`;

    const useWS = cam.serverIP.includes("192.168.0.2");
    const protocol = useWS ? "ws" : "wss";

    const wsUrl = `${protocol}://${
      cam.serverIP
    }/stream/playback/mse?rtsp=${rtsp}&session=${session}&speed=${
      rate ?? playbackSpeedRef.current
    }`;

    console.log(wsUrl);

    return wsUrl;
  };

  const triggerFlash = () => {
    const flash = document.createElement("div");
    flash.className = "flash-overlay";
    document.body.appendChild(flash);

    flash.addEventListener("animationend", () => flash.remove());
  };

  const handleSaveScreens = async () => {
    if (!selectedCameras.some(Boolean)) {
      setShowNoCameraModal(true);
      return;
    }

    // 캡쳐화면 느낌나게
    triggerFlash();

    const ts = dayjs().format("YYYYMMDD_HHmmss");

    await Promise.all(
      selectedCameras.map(async (checked, idx) => {
        if (!checked) return;
        const video = document.getElementById(`mse-video-${idx}`);
        if (!video) return;

        const blob = await captureFrame(video);
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement("a"), {
          href: url,
          download: `camera_${idx + 1}_${ts}.png`,
        });
        a.click();
        URL.revokeObjectURL(url);
      })
    );
  };

  const handleVideoSave = () => {
    if (!selectedCameras.some(Boolean)) {
      setShowNoCameraModal(true);
      return;
    }

    selectedCameras.forEach((checked, idx) => {
      if (!checked) return;

      const videoEl = document.getElementById(`mse-video-${idx}`);
      if (!videoEl) return;

      const ts = dayjs().format("YYYYMMDD_HHmmss");
      recordingVideo(videoEl, `cctv${idx + 1}_${ts}`, 10); // 10초 녹화
    });
  };

  const handleVideoSave2 = () => {
    setShowNoCameraSaveModal(true);
    return;
  };

  const restartAllStreams = () => {
    selectedCameras.forEach((checked, idx) => {
      if (!checked) return;
      stopStreamAsync(idx).then(() => {
        const cam = cameraList[idx];
        const url = isPlaybackRef.current
          ? makePlaybackWsUrl(cam, timeInSecondsRef.current)
          : makeLiveWsUrl(cam);
        startPlay(idx, url);
      });
    });
  };

  /* -------------------- 카메라 목록 받아오기 -------------------- */
  useEffect(() => {
    const fetchCameraLists = async () => {
      try {
        const response = await apiClient.post(
          "/api/RecordingVideo/all-camera-list",
          {
            Page: 1,
            PageSize: 100,
            OrderType: 1,
            WorkplaceId: selectedWorkplaceId,
            LoginUserId: localStorage.getItem("userid"),
          }
        );

        const responseData =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        if (!responseData?.data) {
          console.warn("❌ No camera data found.");
          setCameraLists([]);
          return;
        }

        const cams = responseData.data.map((item) => ({
          cameraID: item.camId,
          cameraName: item.camPosition,
          camUrl: item.camURL,
          serverIP: item.serverIP,
          camUUID: item.camUUID,
          camChannel: item.camChannel,
          recordIP: item.recordIP,
        }));

        setCameraLists(cams);
        setSelectedCameras(new Array(cams.length).fill(false));

        /* 기본 9분할만큼 자동 라이브 재생 */
        setTimeout(() => {
          cams.forEach((cam, idx) => {
            if (idx < gridCount) {
              startPlay(idx, makeLiveWsUrl(cam));
            }
          });
        }, 500);
      } catch (error) {
        console.error("🚨 Failed to fetch camera lists:", error);
      }
    };

    if (selectedWorkplaceId) {
      fetchCameraLists();
    }
  }, [selectedWorkplaceId, currentMenu]);

  /* -------------------- gridCount 변경 시 -------------------- */
  useEffect(() => {
    setSelectedCameras((prev) =>
      cameraList.map((_, idx) => (idx < gridCount ? prev[idx] || false : false))
    );

    setTimeout(() => {
      Promise.all(cameraList.map((_, idx) => stopStreamAsync(idx))).then(() => {
        cameraList.forEach((cam, idx) => {
          if (selectedCameras[idx]) startPlay(idx, makeLiveWsUrl(cam));
        });
      });
    }, 500);
  }, [gridCount, cameraList]);

  const handleCameraToggle = (idx) => {
    setIsCheckboxUpdating(true);

    setSelectedCameras((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];

      const activeCnt = next.filter(Boolean).length;
      if (activeCnt > gridCount) {
        const nextGrid = gridOptions.find((o) => o >= activeCnt);
        if (nextGrid) setGridCount(nextGrid);
      }
      return next;
    });

    const cam = cameraList[idx];
    if (!cam) {
      setIsCheckboxUpdating(false);
      return;
    }

    const videoEl = document.getElementById(`mse-video-${idx}`);
    if (!selectedCameras[idx]) {
      videoEl && (videoEl.style.visibility = "visible");
      startPlay(idx, makeLiveWsUrl(cam));
    }

    setTimeout(() => setIsCheckboxUpdating(false), 100);
  };

  const MAX_SESSION_DURATION = 600000;
  const MAX_CONNECTIONS = 20;

  const reconnectTimers = useRef({});

  const startPlay = (idx, wsUrl) => {
    const videoEl = document.getElementById(`mse-video-${idx}`);
    if (!videoEl) return;

    const mse = new MediaSource();
    videoEl.src = URL.createObjectURL(mse);

    const ws = new WebSocket(wsUrl);
    window[`ws_${idx}`] = ws;
    ws.binaryType = "arraybuffer";

    let mseSourceBuffer = null;
    const pendingBuffers = [];
    let isBufferAppending = false;

    const appendPending = () => {
      const video = document.getElementById(`mse-video-${idx}`);
      if (!video || video.paused) {
        return;
      }

      if (
        !mseSourceBuffer ||
        mseSourceBuffer.updating ||
        mse.readyState !== "open" ||
        mse.sourceBuffers.length === 0
      )
        return;

      const currentTime = video.currentTime;
      const maxBufferedBehind = 10; // 뒤로 10초치만 유지
      if (mseSourceBuffer.buffered.length) {
        const bufferedStart = mseSourceBuffer.buffered.start(0);
        const removeEnd = currentTime - maxBufferedBehind;
        if (removeEnd > bufferedStart) {
          try {
            mseSourceBuffer.remove(bufferedStart, removeEnd);
          } catch (e) {
            console.warn("버퍼 제거 실패", e);
          }
        }
      }

      while (pendingBuffers.length > 0 && !mseSourceBuffer.updating) {
        try {
          mseSourceBuffer.appendBuffer(pendingBuffers.shift());
        } catch (e) {
          console.error("appendBuffer 실패", e);
          break;
        }
      }
      isBufferAppending = false;
    };

    ws.onmessage = (evt) => {
      pendingBuffers.push(evt.data);
      if (!isBufferAppending) {
        isBufferAppending = true;
        requestAnimationFrame(appendPending);
      }
    };

    mse.addEventListener("sourceopen", () => {
      ws.onmessage = (evt) => {
        const data = new Uint8Array(evt.data);
        if (data[0] === 9) {
          const codec = new TextDecoder().decode(data.slice(1));
          if (!mseSourceBuffer) {
            try {
              mseSourceBuffer = mse.addSourceBuffer(
                `video/mp4; codecs=\"${codec}\"`
              );
              mseSourceBuffer.mode = "segments";
              mseSourceBuffer.addEventListener("updateend", appendPending);

              appendPending();

              const video = document.getElementById(`mse-video-${idx}`);
              if (video && video.paused) {
                video
                  .play()
                  .then(() => {
                    video.playbackRate = playbackSpeedRef.current;
                  })
                  .catch((err) => console.warn("play 실패", err));
              }
            } catch (e) {
              console.error("SourceBuffer 초기화 실패", e);
            }
          }
        } else {
          pendingBuffers.push(evt.data);
          if (!isBufferAppending) {
            isBufferAppending = true;
            requestAnimationFrame(appendPending);
          }
        }
      };
    });

    // ws.onclose = () => {
    //   mseSourceBuffer = null;
    //   const cam = cameraList[idx];
    //   if (!cam) return;
    //   const sec = timeInSecondsRef.current;
    //   const newUrl = isPlaybackRef.current
    //     ? makePlaybackWsUrl(cam, sec)
    //     : makeLiveWsUrl(cam);
    //   setTimeout(() => startPlay(idx, newUrl), 1000);
    // };

    const scheduleReconnect = () => {
      // 중복 타이머 방지
      clearTimeout(reconnectTimers.current[idx]);
      reconnectTimers.current[idx] = setTimeout(() => {
        // 재연결 전에 기존 미디어소스 닫아주기
        try {
          mse.endOfStream();
        } catch {}
        const cam = cameraList[idx];
        const sec = timeInSecondsRef.current;
        const url = isPlaybackRef.current
          ? makePlaybackWsUrl(cam, sec)
          : makeLiveWsUrl(cam);
        startPlay(idx, url);
      }, 1000);
    };

    ws.onclose = ws.onerror = () => {
      // playback 중일 때는 전체 재시작
      if (isPlaybackRef.current) {
        console.warn(`카메라 ${idx} 오류 발생, 전체 재시작`);
        restartAllStreams();
      } else {
        // live 모드라면 기존 개별 재연결 스케줄 유지
        scheduleReconnect(idx);
      }
    };
    ws.onerror = () => {
      // 에러에도 같은 재연결
      mseSourceBuffer = null;
      scheduleReconnect();
    };
  };

  useEffect(() => {
    // video error 이벤트 훅
    selectedCameras.forEach((checked, idx) => {
      if (!checked) return;
      const video = document.getElementById(`mse-video-${idx}`);
      if (!video) return;
      video.addEventListener("error", () => {
        console.warn(`video element ${idx} error, 전체 재시작`);
        restartAllStreams();
      });
    });
  }, [selectedCameras, cameraList]);

  useEffect(() => {
    const activeKeys = Object.keys(window).filter((k) => k.startsWith("ws_"));
    const stopAllStreams = activeKeys.map((key) => {
      const idx = parseInt(key.split("_")[1], 10);
      return stopStreamAsync(idx);
    });

    Promise.all(stopAllStreams).then(() => {
      const today = new Date();
      const todayMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      setSelectedDate(todayMidnight);

      // ② 시간 문자열·초값 초기화
      setSelectedTime("00:00:00");
      setTimeInSeconds(0); // ↙︎ 기존 syncTimeline 효과(useEffect)로 슬라이더·CSS var까지 자동 반영

      // ③ LIVE 모드로 전환
      setIsPlayback(false);

      // ④ 재생 중이던 스트림은 모두 LIVE 로 다시 붙이기
      cameraList.forEach((cam, idx) => {
        if (selectedCameras[idx]) {
          stopStreamAsync(idx).then(() => startPlay(idx, makeLiveWsUrl(cam)));
        }
      });
    });
  }, [selectedWorkplaceId]);

  useEffect(() => {
    // 날짜가 바뀌고 재생 중일 경우에만 자동 재생
    if (
      !isCheckboxUpdating &&
      selectedCameras.some(Boolean) &&
      isPlaybackRef.current
    ) {
      const [h, m, s] = selectedTime.split(":").map(Number);
      const targetSec = h * 3600 + m * 60 + s;
      syncTimeline(targetSec);
      setIsPaused(false);

      cameraList.forEach((cam, idx) => {
        if (!selectedCameras[idx]) return;
        const pbWsUrl = makePlaybackWsUrl(cam, targetSec);
        stopStreamAsync(idx).then(() => startPlay(idx, pbWsUrl));
      });
    }
  }, [selectedDate]);

  const stopStreamAsync = (idx) =>
    new Promise((res) => {
      const ws = window[`ws_${idx}`];
      if (!ws) return res();

      ws.onclose = () => {
        delete window[`ws_${idx}`];
        setTimeout(res, 300);
      };
      ws.onerror = () => {
        delete window[`ws_${idx}`];
        setTimeout(res, 300);
      };
      ws.close();
    });

  const cleanUpWebSockets = () => {
    const actives = Object.keys(window).filter((k) => k.startsWith("ws_"));
    if (actives.length > MAX_CONNECTIONS) {
      const oldest = actives[0].split("_")[1];
      stopStreamAsync(oldest);
    }
  };
  setInterval(cleanUpWebSockets, MAX_SESSION_DURATION);

  const playLive = () => {
    if (!selectedCameras.some(Boolean)) {
      setShowNoCameraModal(true);
      return;
    }

    setPlaybackSpeed(1);

    setIsPaused(false);
    setIsPlayback(false);

    /* 오늘 날짜(자정 시각)로 날짜 상태 변경 */
    const now = new Date();
    const todayMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    setSelectedDate(todayMidnight); // ← 날짜를 ‘오늘’로

    /* 시간을 00:00:00 으로 맞추고 타임라인도 0초 지점으로 */
    setSelectedTime("00:00:00"); // 선택 시간 문자 갱신
    syncTimeline(0); // 슬라이더·timeInSeconds 0으로

    cameraList.forEach((cam, idx) => {
      if (selectedCameras[idx])
        stopStreamAsync(idx).then(() => startPlay(idx, makeLiveWsUrl(cam)));
    });
  };

  const getRandomInt = (min, max) =>
    Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min)) + min);

  const session = getRandomInt(1000000, 10000000);

  const now = new Date();
  const isToday = selectedDate.toDateString() === now.toDateString();
  const secondsNow =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const allowedMax = isToday ? Math.max(0, secondsNow - 15 * 60) : maxDuration; // 현재시간 기준으로 15분전까지만 녹화재생 가능
  const timelinePercent = (allowedMax / maxDuration) * 100;

  const pausePlayback = () => {
    setIsPaused(true);
    setIsPlayback(false);

    selectedCameras.forEach((checked, idx) => {
      if (!checked) return;
      const video = document.getElementById(`mse-video-${idx}`);
      if (video && !video.paused) {
        video.pause();
      }
    });
  };

  useEffect(() => {
    if (!isPlayback || isPaused) return;

    const id = setInterval(() => {
      //setTimeInSeconds((prev) => Math.min(prev + 1, allowedMax));
      setTimeInSeconds((prev) =>
        Math.min(prev + playbackSpeedRef.current, allowedMax)
      );
    }, 1000);

    return () => clearInterval(id);
  }, [isPlayback, isPaused, allowedMax, playbackSpeed]);

  const syncTimeline = useCallback(
    (sec) => {
      setTimeInSeconds(sec);
      timeInSecondsRef.current = sec;
      setSelectedTime(formatTime(sec));
      const prog = (sec / allowedMax) * 100;
      document.documentElement.style.setProperty("--progress", `${prog}%`);
    },
    [allowedMax]
  );

  useEffect(() => syncTimeline(timeInSeconds), [timeInSeconds, syncTimeline]);

  const playPlayback = () => {
    if (!selectedCameras.some(Boolean)) {
      setShowNoCameraModal(true);
      return;
    }

    setIsPaused(false);

    const [h, m, s] = selectedTime.split(":").map(Number);
    const targetSec = h * 3600 + m * 60 + s;
    syncTimeline(targetSec);
    setIsPlayback(true);

    cameraList.forEach((cam, idx) => {
      if (!selectedCameras[idx]) return;

      const pbWsUrl = makePlaybackWsUrl(cam, timeInSecondsRef.current);
      stopStreamAsync(idx).then(() => {
        startPlay(idx, pbWsUrl);

        setTimeout(() => {
          const video = document.getElementById(`mse-video-${idx}`);
          if (video && video.paused) {
            video
              .play()
              .then(() => {
                video.playbackRate = playbackSpeedRef.current;
              })
              .catch((e) => console.warn("video play 실패", e));
          }
        }, 500);
      });
    });
  };

  const formatTime = (sec) =>
    [Math.floor(sec / 3600), Math.floor((sec % 3600) / 60), sec % 60]
      .map((v) => String(v).padStart(2, "0"))
      .join(":");

  const handleTimelineChange = (e) => {
    const val = Math.min(Number(e.target.value), allowedMax);
    syncTimeline(val);
  };

  const seekTimer = useRef(null);

  const handleTimelineSeekCommit = React.useCallback(() => {
    clearTimeout(seekTimer.current);
    seekTimer.current = setTimeout(() => playPlayback(), 300);
  }, [playPlayback]);

  const gridItems = useMemo(
    () =>
      Array.from({ length: gridCount }, (_, i) =>
        selectedCameras[i] ? cameraList[i] : null
      ),
    [gridCount, selectedCameras, cameraList]
  );

  const changeSpeed = (rate) => {
    setPlaybackSpeed(rate);
    if (!isPlaybackRef.current) return;

    const sec = timeInSecondsRef.current;

    cameraList.forEach((cam, idx) => {
      if (!selectedCameras[idx]) return;
      const url = makePlaybackWsUrl(cam, sec, rate);
      stopStreamAsync(idx).then(() => startPlay(idx, url));
    });
  };

  //영상확대하기: 더블클릭 하거나, 터치한채 0.7초 지나거나..
  const handleVideoDoubleClick = (idx) => {
    setExpandedIndex((prev) => (prev === idx ? null : idx));
  };
  let longPressTimer = null;

  const handleVideoPressStart = (idx) => {
    longPressTimer = setTimeout(() => {
      setExpandedIndex((prev) => (prev === idx ? null : idx));
    }, 700);
  };

  const handleVideoPressCancel = () => {
    clearTimeout(longPressTimer);
  };

  return (
    <div className="videos_wrapper">
      <div className="videos_topbox">
        <div
          className={`videos_videos ${containVideo ? "contain" : null}`}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.sqrt(gridCount)}, 1fr)`,
          }}
        >
          {gridItems.map((cam, idx) => (
            <div key={idx} className="videos_video">
              <video
                id={`mse-video-${idx}`}
                crossOrigin="anonymous"
                autoPlay
                muted
                playsInline
                onDoubleClick={() => handleVideoDoubleClick(idx)}
                onTouchStart={() => handleVideoPressStart(idx)}
                onTouchEnd={handleVideoPressCancel}
                onTouchMove={handleVideoPressCancel}
                style={{
                  display: selectedCameras[idx] ? "block" : "none",
                  ...(expandedIndex === idx
                    ? {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 500,
                      }
                    : {}),
                }}
              />
              {!selectedCameras[idx] && <img src={cctvSample} alt="video" />}
            </div>
          ))}
        </div>

        <div className="videos_top_right">
          {/* 분할 방식 */}
          <div className="videos_countbtns sectionbox">
            <div className="boxtitle">
              <div>분할방식</div>
              <button
                className="righttop_btn"
                onClick={
                  containVideo
                    ? () => setContainVideo(false)
                    : () => setContainVideo(true)
                }
              >
                창에 맞추기
              </button>
            </div>
            <div className="videos_box_btns">
              {gridOptions.map((o) => (
                <button
                  key={o}
                  className={gridCount === o ? "on" : ""}
                  onClick={() => setGridCount(o)}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          {/* 카메라 목록 */}
          <div className="videos_list sectionbox">
            <div className="boxtitle">카메라목록</div>
            <div className="box_list">
              <div className="box_list_headers">
                <div className="box_list_header" style={{ flex: 3 }}>
                  카메라이름
                </div>
                <div className="box_list_header" style={{ flex: 1 }}>
                  표시여부
                </div>
              </div>
              <div className="box_list_body">
                {cameraList.length === 0 ? (
                  <div>카메라 정보가 없습니다</div>
                ) : (
                  cameraList.map((cam, idx) => (
                    <div key={idx} className="box_list_row">
                      <div className="box_list_column" style={{ flex: 3 }}>
                        {cam.cameraName}
                      </div>
                      <div className="box_list_column" style={{ flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={selectedCameras[idx] || false}
                          onChange={() => handleCameraToggle(idx)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="videos_bottombox">
        <div className="videos_calendar">
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale="ko-KR"
            formatMonthYear={(l, d) =>
              `${d.getFullYear()}년 ${d.getMonth() + 1}월`
            }
            formatDay={(l, d) => d.getDate()}
            next2Label={null}
            prev2Label={null}
            tileDisabled={({ date, view }) => {
              const today = new Date();
              return view === "month" && date > today;
            }}
            tileClassName={({ date, view }) => {
              if (view !== "month") return "";
              const today = new Date();
              const isToday = date.toDateString() === today.toDateString();
              const isSel = date.toDateString() === selectedDate.toDateString();
              return isToday
                ? "calendar-today"
                : isSel
                ? "calendar-selected"
                : "";
            }}
          />
        </div>

        <div className="videos_bottom_right">
          <div className="videos_explorer_btns">
            <div className="videos_playbtns">
              {!(isPlayback && !isPaused) && (
                <button className="btn_back_blue" onClick={playPlayback}>
                  <IonIcon
                    name="IoPlayCircle"
                    size={26}
                    color="rgba(255,255,255,.6)"
                  />
                  재생하기
                </button>
              )}
              {isPlayback && !isPaused && (
                <button className="btn_back_red" onClick={pausePlayback}>
                  <IonIcon
                    name="IoPause"
                    size={22}
                    color="rgba(255,255,255,.6)"
                  />
                  일시정지
                </button>
              )}
              {isPlayback && (
                <>
                  <button className="btn_back_green" onClick={playLive}>
                    <IonIcon
                      name="IoRefresh"
                      size={22}
                      color="rgba(255,255,255,.6)"
                    />
                    LIVE
                  </button>
                </>
              )}{" "}
              {/* 2배속, 3배속..이거 기능 체크해야 함 */}
              {isPlayback && (
                <>
                  <button
                    onClick={() => changeSpeed(1)}
                    disabled={playbackSpeed === 1}
                    className={
                      playbackSpeed === 1 ? "speed-btn active" : "speed-btn"
                    }
                  >
                    <IonIcon
                      name="IoPlay"
                      size={22}
                      color="rgba(255,255,255,.6)"
                    />{" "}
                    1배속
                  </button>
                  <button
                    onClick={() => changeSpeed(2.0)}
                    disabled={playbackSpeed === 2.0}
                    className={
                      playbackSpeed === 2.0 ? "speed-btn active" : "speed-btn"
                    }
                  >
                    <IonIcon
                      name="IoPlayForward"
                      size={22}
                      color="rgba(255,255,255,.6)"
                    />
                    2배속
                  </button>
                  <button
                    onClick={() => changeSpeed(4)}
                    disabled={playbackSpeed === 4}
                    className={
                      playbackSpeed === 4 ? "speed-btn active" : "speed-btn"
                    }
                  >
                    <IonIcon
                      name="IoPlayForward"
                      size={22}
                      color="rgba(255,255,255,.6)"
                    />
                    4배속
                  </button>
                </>
              )}
            </div>

            <div className="videos_savebtns">
              <span className="playerdateTime">
                <span className="player_date">
                  {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월{" "}
                  {selectedDate.getDate()}일
                </span>
                <span className="player_time">{selectedTime}</span>
              </span>
              {/* 2025.07.14.월 보안대책 이행실적 증빙 */}
              {/* <button onClick={handleVideoSave}>
                <IonIcon
                  name="IoVideocamOutline"
                  size={22}
                  color="rgba(255,255,255,.6)"
                />
                영상저장
              </button> */}
              <button onClick={handleVideoSave2}>
                <IonIcon
                  name="IoVideocamOutline"
                  size={22}
                  color="rgba(255,255,255,.6)"
                />
                영상저장
              </button>
              <button onClick={handleSaveScreens}>
                <IonIcon
                  name="IoImageOutline"
                  size={22}
                  color="rgba(255,255,255,.6)"
                />
                화면저장
              </button>
            </div>
          </div>

          <div className="videos_explorer sectionbox">
            <div className="timeline_back" style={{ position: "relative" }}>
              <img src={timeline} alt="timeline" style={{ width: "100%" }} />
              {timelinePercent < 100 && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: `${100 - timelinePercent}%`,
                    height: "100%",
                    background: "rgba(0,0,0,0.4)",
                    pointerEvents: "none",
                    display: "none",
                  }}
                />
              )}
              <input
                type="range"
                min="0"
                max={allowedMax}
                value={timeInSeconds}
                onChange={handleTimelineChange}
                onMouseUp={handleTimelineSeekCommit}
                className="timeline_slider"
                style={{ width: `${timelinePercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {showNoCameraModal && (
        <div
          onClick={() => setShowNoCameraModal(false)}
          className="modal_dimmbox"
        >
          <div onClick={(e) => e.stopPropagation()} className="popup">
            <div className="popheader">
              <div className="titlebox">
                <div className="poptitle">알림</div>
                <button onClick={() => setShowNoCameraModal(false)}>
                  <IonIcon name="IoClose" size={40} />
                </button>
              </div>
            </div>
            <div className="popbody">
              <div className="pop_textbox">
                재생할 카메라를 먼저 선택해주세요.
              </div>
            </div>
            <div className="popfooter">
              <div className="bottombtns pop">
                <button onClick={() => setShowNoCameraModal(false)}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNoCameraSaveModal && (
        <div
          onClick={() => setShowNoCameraSaveModal(false)}
          className="modal_dimmbox"
        >
          <div onClick={(e) => e.stopPropagation()} className="popup">
            <div className="popheader">
              <div className="titlebox">
                <div className="poptitle">알림</div>
                <button onClick={() => setShowNoCameraSaveModal(false)}>
                  <IonIcon name="IoClose" size={40} />
                </button>
              </div>
            </div>
            <div className="popbody">
              <div className="pop_textbox">영상 저장은 사용할 수 없습니다.</div>
            </div>
            <div className="popfooter">
              <div className="bottombtns pop">
                <button onClick={() => setShowNoCameraSaveModal(false)}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="modal" onClick={() => setShowPreviewModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <p>준비중입니다.</p>
            <button onClick={() => setShowPreviewModal(false)}>닫기</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingVideo;
